import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Plus, Pencil, Trash2, Shield, X, Check, Trophy, HelpCircle } from 'lucide-react';
import apiClient from '../services/apiClient';
import StakeTable, { Column } from './GameEngine/StakeTable';

// ─── Types ────────────────────────────────────────────────────────────────────
interface VipLevel {
  id: number;
  level_name: string;
  level_number: number;
  condition_type: 'bet' | 'deposit' | 'both';
  required_amount: number;
  required_deposit?: number;
  badge_color: string;
  tier: string;
  perks: string[];
  is_active: boolean;
  sort_order: number;
}

const tierOptions = [
  { value: 'bronze', label: 'Bronze', color: '#cd7f32' },
  { value: 'silver', label: 'Silver', color: '#c0c0c0' },
  { value: 'gold', label: 'Gold', color: '#ffd700' },
  { value: 'platinum', label: 'Platinum', color: '#9370db' },
  { value: 'diamond', label: 'Diamond', color: '#dc8abb' },
];

// ─── Form Modal ───────────────────────────────────────────────────────────────
const LevelModal: React.FC<{
  level: Partial<VipLevel> | null;
  onClose: () => void;
  onSave: (data: Partial<VipLevel>) => Promise<void>;
}> = ({ level, onClose, onSave }) => {
  const isEdit = !!level?.id;
  const [form, setForm] = useState<Partial<VipLevel>>({
    level_name: '',
    level_number: 1,
    condition_type: 'bet',
    required_amount: 0,
    badge_color: '#cd7f32',
    tier: 'bronze',
    perks: [],
    is_active: true,
    sort_order: 0,
    ...level,
  });
  const [perkInput, setPerkInput] = useState('');
  const [saving, setSaving] = useState(false);

  const handleChange = (key: keyof VipLevel, value: any) => {
    setForm(prev => ({ ...prev, [key]: value }));
    // Auto-set badge color when tier changes
    if (key === 'tier') {
      const t = tierOptions.find(t => t.value === value);
      if (t) setForm(prev => ({ ...prev, tier: value, badge_color: t.color }));
    }
  };

  const addPerk = () => {
    if (!perkInput.trim()) return;
    setForm(prev => ({ ...prev, perks: [...(prev.perks || []), perkInput.trim()] }));
    setPerkInput('');
  };

  const removePerk = (i: number) => {
    setForm(prev => ({ ...prev, perks: (prev.perks || []).filter((_, idx) => idx !== i) }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.level_name?.trim()) return toast.error('Level name is required');
    setSaving(true);
    try {
      await onSave(form);
    } catch {
      // toast is handled in parent
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-700">
          <div className="flex items-center gap-2">
            <Shield size={20} className="text-purple-400" />
            <h2 className="text-white font-bold text-base">{isEdit ? 'Edit VIP Level' : 'Add VIP Level'}</h2>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Level Name + Number */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 font-medium mb-1 block">Level Name *</label>
              <input
                type="text"
                value={form.level_name || ''}
                onChange={e => handleChange('level_name', e.target.value)}
                placeholder="e.g. Bronze"
                className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500 transition-colors"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 font-medium mb-1 block">Level Number</label>
              <input
                type="number"
                value={form.level_number || 0}
                onChange={e => handleChange('level_number', parseInt(e.target.value) || 0)}
                className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500 transition-colors"
              />
            </div>
          </div>

          {/* Tier */}
          <div>
            <label className="text-xs text-slate-400 font-medium mb-1.5 block">Tier</label>
            <div className="flex gap-2 flex-wrap">
              {tierOptions.map(t => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => handleChange('tier', t.value)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                    form.tier === t.value
                      ? 'border-white/50 text-white shadow-lg'
                      : 'border-slate-600 text-slate-400 hover:border-slate-500'
                  }`}
                  style={{ background: form.tier === t.value ? t.color + '33' : undefined }}
                >
                  <span style={{ color: t.color }}>●</span> {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Condition Type + Amount */}
          {form.condition_type === 'both' ? (
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-slate-400 font-medium mb-1 block">Unlock Condition</label>
                <select
                  value={form.condition_type}
                  onChange={e => handleChange('condition_type', e.target.value)}
                  className="w-full bg-slate-800 border border-slate-600 rounded-lg px-2 py-2 text-white text-xs focus:outline-none focus:border-purple-500 transition-colors"
                >
                  <option value="bet">Total Bet Amount</option>
                  <option value="deposit">Total Deposit Amount</option>
                  <option value="both">Both (Bet & Deposit)</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-400 font-medium mb-1 block">Required Wager ($)</label>
                <input
                  type="number"
                  min="0"
                  value={form.required_amount || 0}
                  onChange={e => handleChange('required_amount', parseFloat(e.target.value) || 0)}
                  className="w-full bg-slate-800 border border-slate-600 rounded-lg px-2 py-2 text-white text-xs focus:outline-none focus:border-purple-500 transition-colors"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 font-medium mb-1 block">Required Deposit ($)</label>
                <input
                  type="number"
                  min="0"
                  value={form.required_deposit || 0}
                  onChange={e => handleChange('required_deposit', parseFloat(e.target.value) || 0)}
                  className="w-full bg-slate-800 border border-slate-600 rounded-lg px-2 py-2 text-white text-xs focus:outline-none focus:border-purple-500 transition-colors"
                />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-400 font-medium mb-1 block">Unlock Condition</label>
                <select
                  value={form.condition_type || 'bet'}
                  onChange={e => handleChange('condition_type', e.target.value)}
                  className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500 transition-colors"
                >
                  <option value="bet">Total Bet Amount</option>
                  <option value="deposit">Total Deposit Amount</option>
                  <option value="both">Both (Bet & Deposit)</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-400 font-medium mb-1 block">Required Amount ($)</label>
                <input
                  type="number"
                  min="0"
                  value={form.required_amount || 0}
                  onChange={e => handleChange('required_amount', parseFloat(e.target.value) || 0)}
                  className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500 transition-colors"
                />
              </div>
            </div>
          )}

          {/* Sort Order + Active */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 font-medium mb-1 block">Sort Order</label>
              <input
                type="number"
                value={form.sort_order || 0}
                onChange={e => handleChange('sort_order', parseInt(e.target.value) || 0)}
                className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500 transition-colors"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 font-medium mb-1 block">Status</label>
              <button
                type="button"
                onClick={() => handleChange('is_active', !form.is_active)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-all w-full ${
                  form.is_active
                    ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-400'
                    : 'bg-red-500/20 border-red-500/40 text-red-400'
                }`}
              >
                <span className={`w-3 h-3 rounded-full ${form.is_active ? 'bg-emerald-400' : 'bg-red-400'}`} />
                {form.is_active ? 'Active' : 'Inactive'}
              </button>
            </div>
          </div>

          {/* Perks */}
          <div>
            <label className="text-xs text-slate-400 font-medium mb-1.5 block">Perks / Benefits</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={perkInput}
                onChange={e => setPerkInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addPerk(); } }}
                placeholder="e.g. Weekly Rakeback"
                className="flex-1 bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500 transition-colors"
              />
              <button type="button" onClick={addPerk} className="px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors">
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto p-1 bg-slate-950/40 border border-slate-800 rounded-lg">
              {(form.perks || []).map((perk, i) => (
                <span key={i} className="flex items-center gap-1 bg-slate-700 text-slate-300 text-xs px-2.5 py-1 rounded-full">
                  {perk}
                  <button type="button" onClick={() => removePerk(i)} className="text-slate-400 hover:text-red-400 ml-0.5">
                    <X size={10} />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-white rounded-xl text-sm font-medium transition-colors">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700 text-white rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {saving ? (
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : <Check size={16} />}
              {isEdit ? 'Save Changes' : 'Create Level'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const VipLevels: React.FC = () => {
  const [levels, setLevels] = useState<VipLevel[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalLevel, setModalLevel] = useState<Partial<VipLevel> | null | false>(false);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  const [config, setConfig] = useState<any>(null);
  const [tooltipText, setTooltipText] = useState('');
  const [vipPageHeading, setVipPageHeading] = useState('');
  const [vipPageSubtitle, setVipPageSubtitle] = useState('');
  const [activeDesign, setActiveDesign] = useState('bc');
  const [savingTooltip, setSavingTooltip] = useState(false);

  const fetchConfig = async () => {
    try {
      const res = await apiClient.get('/admin/websiteconfig');
      const data = res.data?.data?.websiteConfig || {};
      setConfig(data);
      setTooltipText(data.vip_tooltip_text || '');
      setVipPageHeading(data.vip_page_heading || '');
      setVipPageSubtitle(data.vip_page_subtitle || '');
      setActiveDesign(data.active_design || 'bc');
    } catch (err) {
      console.error('Failed to load website config:', err);
    }
  };

  const fetchLevels = async () => {
    try {
      const res = await apiClient.get('/admin/vip-levels');
      if (res.data?.success) setLevels(res.data.data);
    } catch {
      toast.error('Failed to load VIP levels');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLevels();
    fetchConfig();
  }, []);

  const handleSave = async (data: Partial<VipLevel>) => {
    try {
      if (data.id) {
        await apiClient.put(`/admin/vip-levels/${data.id}`, data);
        toast.success('Level updated successfully');
      } else {
        await apiClient.post('/admin/vip-levels', data);
        toast.success('Level created successfully');
      }
      setModalLevel(false);
      fetchLevels();
    } catch {
      toast.error('Failed to save level');
      throw new Error('save failed');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await apiClient.delete(`/admin/vip-levels/${id}`);
      toast.success('Level deleted');
      setDeleteConfirm(null);
      setLevels(prev => prev.filter(l => l.id !== id));
    } catch {
      toast.error('Failed to delete level');
    }
  };

  const toggleActive = async (level: VipLevel) => {
    try {
      await apiClient.put(`/admin/vip-levels/${level.id}`, { ...level, is_active: !level.is_active });
      setLevels(prev => prev.map(l => l.id === level.id ? { ...l, is_active: !l.is_active } : l));
      toast.success(`Level ${!level.is_active ? 'activated' : 'deactivated'}`);
    } catch {
      toast.error('Failed to update status');
    }
  };

  const handleSaveTooltip = async () => {
    if (!config) return;
    setSavingTooltip(true);
    try {
      const updatedConfig = {
        ...config,
        vip_tooltip_text: tooltipText,
        vip_page_heading: vipPageHeading,
        vip_page_subtitle: vipPageSubtitle,
      };
      await apiClient.post('/admin/updatewebsiteconfig', {
        websiteConfig: updatedConfig,
        contactChannels: { dummy: [{ platform: 'dummy', value: 'dummy', is_active: false }] }
      });
      setConfig(updatedConfig);
      toast.success('VIP page config saved successfully!');
    } catch (err) {
      console.error(err);
      toast.error('Failed to save VIP config');
    } finally {
      setSavingTooltip(false);
    }
  };

  const columns: Column[] = [
    {
      key: 'level_number',
      label: 'Lv',
      textAlign: 'center',
      width: 'w-16',
      render: (val, item) => (
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center text-xs font-black shadow-lg mx-auto"
          style={{
            backgroundColor: (item.badge_color || '#cd7f32') + '22',
            border: `1.5px solid ${item.badge_color || '#cd7f32'}55`,
            color: item.badge_color || '#cd7f32',
          }}
        >
          {val}
        </div>
      ),
    },
    {
      key: 'level_name',
      label: 'Level Name',
      render: (val, item) => (
        <div>
          <span className="text-white font-bold block">{val}</span>
          <span className="text-slate-400 text-xs capitalize">{item.tier}</span>
        </div>
      ),
    },
    {
      key: 'required_amount',
      label: 'Unlock Condition',
      render: (val, item) => {
        if (item.condition_type === 'both') {
          return (
            <span className="text-slate-300 font-semibold">
              Both Wager: ${Number(item.required_amount).toLocaleString()} & Deposit: ${Number(item.required_deposit || item.required_amount).toLocaleString()}
            </span>
          );
        }
        return (
          <span className="text-slate-300 font-semibold">
            {item.condition_type === 'deposit' ? 'Total Deposit' : 'Total Bet'}{' '}
            of ${Number(val).toLocaleString()}
          </span>
        );
      },
    },
    {
      key: 'perks',
      label: 'Perks & Benefits',
      render: (val) => {
        const perks = Array.isArray(val) ? val : [];
        if (perks.length === 0) return <span className="text-slate-600 text-xs">-</span>;
        return (
          <div className="flex flex-wrap gap-1 max-w-[280px]">
            {perks.map((p, idx) => (
              <span key={idx} className="text-[10px] bg-slate-800/80 border border-slate-700/60 text-slate-300 px-2 py-0.5 rounded-full shrink-0">
                {p}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      key: 'is_active',
      label: 'Status',
      textAlign: 'center',
      render: (val, item) => (
        <button
          onClick={() => toggleActive(item)}
          className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold border ${
            val
              ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/30'
              : 'bg-red-500/20 text-red-400 border-red-500/30 hover:bg-red-500/30'
          } transition-all`}
        >
          {val ? 'Active' : 'Inactive'}
        </button>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      textAlign: 'center',
      render: (_, item) => (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setModalLevel(item)}
            className="p-1.5 bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white rounded-lg transition-colors border border-slate-700/50"
            title="Edit"
          >
            <Pencil size={13} />
          </button>
          {deleteConfirm === item.id ? (
            <div className="flex items-center gap-1">
              <button
                onClick={() => handleDelete(item.id)}
                className="p-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                title="Confirm Delete"
              >
                <Check size={13} />
              </button>
              <button
                onClick={() => setDeleteConfirm(null)}
                className="p-1.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                title="Cancel"
              >
                <X size={13} />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setDeleteConfirm(item.id)}
              className="p-1.5 bg-slate-800 hover:bg-red-900/50 text-slate-400 hover:text-red-400 rounded-lg transition-colors border border-slate-700/50"
              title="Delete"
            >
              <Trash2 size={13} />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="ml-0 lg:ml-[260px] p-4 sm:p-6 lg:p-8 min-h-screen text-white bg-[#020617]">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
            <Shield size={22} className="text-purple-400" />
            VIP Levels Management
          </h1>
          <p className="text-slate-400 text-xs mt-0.5">Configure VIP tiers, unlock conditions and rewards</p>
        </div>
        <button
          onClick={() => setModalLevel({})}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-semibold transition-all shadow-lg shadow-purple-900/20"
        >
          <Plus size={16} /> Add Level
        </button>
      </div>

      {/* Stats Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Total Levels', value: levels.length, color: 'text-purple-400' },
          { label: 'Active Levels', value: levels.filter(l => l.is_active).length, color: 'text-emerald-400' },
          { label: 'Bet-Based', value: levels.filter(l => l.condition_type === 'bet').length, color: 'text-blue-400' },
          { label: 'Deposit-Based', value: levels.filter(l => l.condition_type === 'deposit').length, color: 'text-amber-400' },
        ].map(stat => (
          <div key={stat.label} className="bg-slate-900 border border-slate-800/80 rounded-xl p-4 shadow-sm">
            <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
            <div className="text-slate-400 text-xs mt-0.5">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Levels Table */}
      <div className="mb-6">
        <StakeTable
          title=""
          columns={columns}
          data={levels}
          emptyMessage="No VIP levels found."
          hasSidebarMargin={false}
          isLoading={loading}
          searchPlaceholder="Search VIP levels..."
        />
      </div>

      {activeDesign === 'stake' && (
        <div className="bg-slate-900 border border-slate-800/80 rounded-2xl p-5 mb-6 w-full">
          <h2 className="text-white font-bold text-sm mb-4 flex items-center gap-2">
            <Trophy size={16} className="text-purple-400" />
            Stake Theme: VIP Club Page Config
          </h2>
          <div className="space-y-4">

            {/* VIP Page Heading */}
            <div>
              <label className="block text-slate-400 text-xs font-semibold mb-1.5">
                VIP Club Page Heading (Logged-out Hero)
              </label>
              <input
                type="text"
                value={vipPageHeading}
                onChange={(e) => setVipPageHeading(e.target.value)}
                className="w-full bg-slate-950 text-white rounded-lg p-3 border border-slate-800 focus:outline-none focus:ring-2 focus:ring-purple-500 text-xs"
                placeholder="e.g. The unrivalled VIP experience"
              />
              <p className="text-slate-500 text-[10px] mt-1">VIP Club page par logged-out hero section ka main heading.</p>
            </div>

            {/* VIP Page Subtitle */}
            <div>
              <label className="block text-slate-400 text-xs font-semibold mb-1.5">
                VIP Club Page Subtitle (Logged-out Hero)
              </label>
              <input
                type="text"
                value={vipPageSubtitle}
                onChange={(e) => setVipPageSubtitle(e.target.value)}
                className="w-full bg-slate-950 text-white rounded-lg p-3 border border-slate-800 focus:outline-none focus:ring-2 focus:ring-purple-500 text-xs"
                placeholder="e.g. Unlock exclusive benefits and receive instantly withdrawable bonuses without any strings attached"
              />
              <p className="text-slate-500 text-[10px] mt-1">Heading ke neeche wala paragraph text.</p>
            </div>

            {/* Tooltip Text */}
            <div>
              <label className="block text-slate-400 text-xs font-semibold mb-1.5 flex items-center gap-1.5">
                <HelpCircle size={14} className="text-purple-400" />
                VIP Progress Tooltip Text (Home Page Info Icon)
              </label>
              <textarea
                rows={3}
                value={tooltipText}
                onChange={(e) => setTooltipText(e.target.value)}
                className="w-full bg-slate-950 text-white rounded-lg p-3 border border-slate-800 focus:outline-none focus:ring-2 focus:ring-purple-500 text-xs leading-relaxed"
                placeholder="All settled sports and racing bets contribute progress at 3x..."
              />
              <p className="text-slate-500 text-[10px] mt-1">
                Home page par User VIP Progress percentage ke side me i icon click karne par yeh text popover me show hoga.
              </p>
            </div>

            <button
              onClick={handleSaveTooltip}
              disabled={savingTooltip}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-xs font-bold rounded-lg transition-all"
            >
              {savingTooltip ? 'Saving...' : 'Save VIP Config'}
            </button>
          </div>
        </div>
      )}

      {/* Modal */}
      {modalLevel !== false && (
        <LevelModal
          level={modalLevel}
          onClose={() => setModalLevel(false)}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default VipLevels;
